import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { StatusBadge } from "../components/StatusBadge";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import { UploadCloud, FileText, X, ArrowRight, RotateCw, Layers, CopyX, Building2 } from "lucide-react";

export default function UploadPage() {
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sources, setSources] = useState([]); // source upload jobs
  const [dupPrompt, setDupPrompt] = useState(null); // { files: File[], names: string[] }
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const addFiles = (fileList) => {
    const pdfs = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    const rejected = Array.from(fileList).length - pdfs.length;
    if (rejected > 0) toast.error(`${rejected} non-PDF file(s) ignored`);
    if (pdfs.length === 0) return;
    setQueue((q) => [...q, ...pdfs.map((f) => ({ file: f, name: f.name, size: f.size }))]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFromQueue = (idx) => setQueue((q) => q.filter((_, i) => i !== idx));

  // Upload a set of File objects. `replace` lists exact filenames that should overwrite existing ones.
  const handleUpload = async (filesToUpload, replace = []) => {
    if (!filesToUpload.length) return;
    setUploading(true);
    try {
      const res = await api.uploadDocuments(filesToUpload, replace);
      const items = res.data.uploaded || [];
      const ok = items.filter((u) => !u.error && u.status !== "duplicate");
      const dupNames = items.filter((u) => u.status === "duplicate").map((u) => u.filename);
      const errs = items.filter((u) => u.error);

      if (ok.length) {
        setSources((prev) => [...ok.map((u) => ({ ...u, child_documents: [] })), ...prev]);
        toast.success(`${ok.length} file(s) uploaded — splitting by authority`);
      }
      errs.forEach((e) => toast.error(`${e.filename}: ${e.error}`));

      if (dupNames.length) {
        const dupFiles = filesToUpload.filter((f) => dupNames.includes(f.name));
        setDupPrompt({ files: dupFiles, names: dupNames });
      }
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const startUpload = async () => {
    if (queue.length === 0) return;
    const files = queue.map((q) => q.file);
    setQueue([]);
    await handleUpload(files, []);
  };

  const confirmReplace = async () => {
    if (!dupPrompt) return;
    const { files, names } = dupPrompt;
    setDupPrompt(null);
    await handleUpload(files, names);
  };

  const cancelReplace = () => setDupPrompt(null);

  const refreshSources = useCallback(async () => {
    if (sources.length === 0) return;
    const updated = await Promise.all(
      sources.map(async (s) => {
        try {
          const res = await api.getUpload(s.id);
          return res.data;
        } catch {
          return s;
        }
      })
    );
    setSources(updated);
  }, [sources.length]); // eslint-disable-line

  useEffect(() => {
    if (sources.length === 0) return;
    const anyProcessing = sources.some((s) => s.status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(refreshSources, 4000);
    return () => clearInterval(t);
  }, [sources, refreshSources]);

  const fmtSize = (b) => (b ? `${(b / 1024 / 1024).toFixed(2)} MB` : "");

  return (
    <div className="space-y-6">
      <PageHeader title="Upload" subtitle="Add toll-bill PDFs (scanned or digital). Each bill is auto-split into its own document." />

      <Card>
        <CardContent className="p-5 sm:p-6">
          <div
            data-testid="pdf-dropzone"
            tabIndex={0}
            role="button"
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`rounded-xl border border-dashed p-8 sm:p-12 text-center cursor-pointer transition-[background-color,border-color] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${
              dragOver ? "border-primary bg-accent" : "border-border bg-card hover:border-[hsl(var(--ring))]"
            }`}
          >
            <div className="flex flex-col items-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent mb-3">
                <UploadCloud className="h-7 w-7 text-primary" />
              </div>
              <div className="font-heading font-semibold text-base">Drop toll-bill PDFs here, or browse</div>
              <div className="mt-1 text-sm text-muted-foreground">Multi-bill PDFs are split by authority into separate documents · PDF only</div>
              <Button type="button" variant="outline" className="mt-4" data-testid="pdf-browse-button" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>
                Browse files
              </Button>
            </div>
            <input ref={inputRef} type="file" accept="application/pdf" multiple hidden onChange={(e) => addFiles(e.target.files)} data-testid="pdf-file-input" />
          </div>

          {queue.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">{queue.length} file(s) ready</div>
                <Button size="sm" disabled={uploading} onClick={startUpload} data-testid="start-upload-button" className="gap-2">
                  {uploading ? "Uploading..." : "Extract & split"} <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <ScrollArea className="max-h-56">
                <div className="space-y-2" data-testid="upload-queue">
                  {queue.map((q, i) => (
                    <div key={`${q.name}-${q.size}-${q.lastModified ?? i}`} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 bg-card">
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate flex-1">{q.name}</span>
                      <span className="text-xs text-muted-foreground">{fmtSize(q.size)}</span>
                      <button onClick={() => removeFromQueue(i)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </CardContent>
      </Card>

      {sources.length > 0 && (
        <div className="space-y-4">
          {sources.map((s) => (
            <Card key={s.id} data-testid="upload-result-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{s.filename}</div>
                    {s.status === "processing" && (
                      <div className="text-xs text-muted-foreground">Extracting & splitting bills by authority…</div>
                    )}
                    {s.status === "completed" && (
                      <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                        <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {s.child_count} document(s) created</span>
                        {s.duplicate_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-amber-700"><CopyX className="h-3.5 w-3.5" /> {s.duplicate_count} duplicate(s) skipped</span>
                        )}
                      </div>
                    )}
                    {s.status === "failed" && <div className="text-xs text-destructive">{s.error || "Extraction failed"}</div>}
                  </div>
                  <StatusBadge status={s.status} />
                </div>

                {s.status === "processing" && <Progress value={55} className="h-1.5" />}

                {s.status === "completed" && (s.child_documents || []).length > 0 && (
                  <div className="space-y-2">
                    {s.child_documents.map((c) => (
                      <div key={c.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid="upload-child-row">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent shrink-0">
                          <Building2 className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{c.issuing_authority || c.filename}</div>
                          <div className="text-xs text-muted-foreground">Pages {c.page_start}–{c.page_end} · {c.page_count} page(s)</div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/documents/${c.id}`)} data-testid="view-child-button">View</Button>
                      </div>
                    ))}
                  </div>
                )}

                {s.status === "completed" && s.child_count === 0 && s.duplicate_count > 0 && (
                  <div className="text-sm text-muted-foreground">All bills in this file already exist — nothing new added.</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!dupPrompt} onOpenChange={(o) => !o && cancelReplace()}>
        <AlertDialogContent data-testid="duplicate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>File already exists</AlertDialogTitle>
            <AlertDialogDescription>
              {dupPrompt && dupPrompt.names.length === 1
                ? `A file named "${dupPrompt.names[0]}" was already uploaded. Replace it? The existing upload and its extracted bills will be removed and the new file processed fresh.`
                : `${dupPrompt ? dupPrompt.names.length : 0} files with these names were already uploaded. Replace them? The existing uploads and their extracted bills will be removed and the new files processed fresh.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {dupPrompt && dupPrompt.names.length > 1 && (
            <ul className="max-h-32 overflow-auto rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm space-y-1">
              {dupPrompt.names.map((n) => (
                <li key={n} className="flex items-center gap-2 truncate">
                  <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="truncate">{n}</span>
                </li>
              ))}
            </ul>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="duplicate-keep-button">Keep existing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReplace} data-testid="duplicate-replace-button">
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
